<template>
  <v-container fluid class="pa-0 rr-pass-reset-page">
    <div class="rr-pass-reset-toolbar bg-red-darken-3 py-2 py-md-3 px-3 px-md-6">
      <img
        :src="rrLogo"
        height="36"
        width="36"
        class="flex-shrink-0"
        :alt="$t('rete_ricambi_pass_reset.logo_alt')"
      />
      <div
        class="text-subtitle-1 text-white text-truncate flex-grow-1 min-width-0"
      >
        {{ $t("rete_ricambi_pass_reset.title") }}
      </div>
    </div>

    <div class="internal-admin pa-3 pa-sm-4 pa-md-6">
      <p class="internal-admin__hint mb-4">
        {{ $t("rete_ricambi_pass_reset.hint_before") }}<strong>{{ $t("rete_ricambi_pass_reset.hint_emphasis") }}</strong>{{ $t("rete_ricambi_pass_reset.hint_after") }}
      </p>

      <v-form class="internal-admin__form" @submit.prevent="resetPassword">
        <v-text-field
          v-model="email"
          :label="$t('rete_ricambi_pass_reset.email_label')"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :placeholder="placeholderEmailExample"
          autocomplete="off"
          class="mb-4"
        />
        <v-text-field
          v-model="password"
          :label="$t('rete_ricambi_pass_reset.password_label')"
          :type="showPassword ? 'text' : 'password'"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          autocomplete="new-password"
          class="mb-4"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword"
        />
        <v-btn
          type="submit"
          color="#208f5c"
          class="text-white internal-admin__submit-btn"
          variant="flat"
          :loading="loading"
        >
          {{ $t("rete_ricambi_pass_reset.submit_button") }}
        </v-btn>
      </v-form>

      <v-alert
        v-if="successMessage"
        type="success"
        variant="tonal"
        class="mt-4"
        border="start"
        prominent
      >
        {{ successMessage }}
      </v-alert>
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        class="mt-4"
        border="start"
        prominent
      >
        {{ errorMessage }}
      </v-alert>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import rrLogo from "@/assets/rr-logo.png";
import config from "@/config";

function extractApiErrorMessage(data) {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (typeof data.message === "string" && data.message) return data.message;
  if (data.data && typeof data.data === "object" && typeof data.data.message === "string") {
    return data.data.message;
  }
  return "";
}

function authHeadersJson() {
  const token = localStorage.getItem("token");
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = token;
  return headers;
}

function generatePasswordFromEmail(email) {
  const trimmed = (email || "").trim();
  const atIndex = trimmed.indexOf("@");
  if (atIndex <= 0) return "";
  const localPart = trimmed.slice(0, atIndex);
  const day = String(new Date().getDate()).padStart(2, "0");
  return `${localPart}_${day}A`;
}

export default {
  name: "ReteRicambiPassReset",

  data() {
    return {
      rrLogo,
      placeholderEmailExample: "user@example.com",
      email: "",
      password: "",
      showPassword: true,
      loading: false,
      successMessage: "",
      errorMessage: "",
    };
  },

  computed: {
    apiBaseUrl() {
      return config.getServerUri();
    },
  },

  watch: {
    email(value) {
      const generated = generatePasswordFromEmail(value);
      if (generated) {
        this.password = generated;
        this.showPassword = true;
      } else if (!(value || "").trim()) {
        this.password = "";
      }
    },
  },

  methods: {
    axiosErrorMessage(error, fallbackKey) {
      const status = error?.response?.status;
      const raw = error?.response?.data;
      const text = extractApiErrorMessage(raw);
      const t = (key) => this.$t(`rete_ricambi_pass_reset.${key}`);
      if (status === 401) return t("error_auth");
      if (status === 403) return t("error_forbidden");
      if (status === 404) return t("error_not_found");
      if (status === 503) return text || t("error_db_not_configured");
      if (status === 400 && text) return text;
      if (text) return text;
      return t(fallbackKey);
    },

    resetPassword() {
      this.successMessage = "";
      this.errorMessage = "";

      const email = (this.email || "").trim();
      const password = this.password || "";

      if (!email) {
        this.errorMessage = this.$t("rete_ricambi_pass_reset.error_enter_email");
        return;
      }
      if (!password.trim()) {
        this.errorMessage = this.$t("rete_ricambi_pass_reset.error_enter_password");
        return;
      }

      this.loading = true;
      axios
        .post(
          `${this.apiBaseUrl}/rete-ricambi/password`,
          { email, password },
          { headers: authHeadersJson() },
        )
        .then((res) => {
          const message =
            res.data?.message || this.$t("rete_ricambi_pass_reset.success_default");
          this.successMessage = message;
          this.password = generatePasswordFromEmail(email);
          this.showPassword = true;
        })
        .catch((error) => {
          this.errorMessage = this.axiosErrorMessage(error, "error_reset_failed");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.rr-pass-reset-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  row-gap: 8px;
}

.rr-pass-reset-page .min-width-0 {
  min-width: 0;
}

.internal-admin {
  max-width: 560px;
  margin: 0 auto;
  min-height: min(320px, 50vh);
}

.internal-admin__hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
}

.internal-admin__submit-btn {
  width: 100%;
}

@media (min-width: 600px) {
  .internal-admin__submit-btn {
    width: auto;
  }
}
</style>
